import type { AudioFormat, ProfileContext } from '@namu/protocol'
import type { LocaleCode } from '@/i18n/locales'
import type { ApiClient } from '@/api/client'
import { DEFAULT_PERSONA } from '@/persona/personas'
import type { PersonaId } from '@/persona/personas'
import { CAPTURE_SAMPLE_RATE, createCapture } from '../audio/capture'
import type { Capture } from '../audio/capture'
import { preferredCodec } from '../audio/codec'
import { createPlayback } from '../audio/playback'
import type { Playback } from '../audio/playback'
import { createRealtimeClient } from '../realtime/RealtimeClient'
import type { RealtimeClient, SocketFactory } from '../realtime/RealtimeClient'
import { VoiceError } from '../types'
import type {
  ConnectOptions,
  ListenOptions,
  ListenResult,
  SpeakOptions,
  SpeakResult,
  VoiceCapabilities,
  VoiceEvent,
  VoiceListener,
  VoiceTransport,
} from '../types'

/**
 * Namu's own voice, over the WebSocket.
 *
 * This is the one transport where the assistant is a participant rather than a
 * puppet. The other two are told what to say; this one is talking to a model
 * that decides for itself, hears the person hesitate, and can answer before
 * they have finished. That is why `duplex` exists as a capability: the screen
 * has to know not to wrap a turn-taking loop around it.
 *
 * `speak` and `listen` are still here because the interface requires them, and
 * both are honest about doing nothing. Making them throw would be worse: the
 * shared components that show captions and levels call into this object, and
 * they should not each need to know which transport they got.
 */

export interface RealtimeTransportOptions {
  api: ApiClient
  /** The microphone the session already opened. Never opens its own. */
  stream: MediaStream | null
  profile?: ProfileContext | undefined
  socketFactory?: SocketFactory
}

export class RealtimeTransport implements VoiceTransport {
  readonly id = 'realtime'

  private readonly api: ApiClient
  private readonly listeners = new Set<VoiceListener>()
  private readonly socketFactory: SocketFactory | undefined
  private profile: ProfileContext | undefined

  private stream: MediaStream | null
  private client: RealtimeClient | null = null
  private capture: Capture | null = null
  private playback: Playback | null = null

  private locale: LocaleCode = 'en'
  private persona: PersonaId = DEFAULT_PERSONA
  /** The codec the server negotiated, kept for a capture built later. */
  private outputCodec: AudioFormat['codec'] | null = null
  private muted = false
  private connected = false
  private modelBacked = false

  /** The client's own turn counter, stamped on outgoing audio frames. */
  private outgoingTurn = 0
  private keepalive: ReturnType<typeof setInterval> | null = null

  constructor(options: RealtimeTransportOptions) {
    this.api = options.api
    this.stream = options.stream
    this.profile = options.profile
    this.socketFactory = options.socketFactory
  }

  get capabilities(): VoiceCapabilities {
    return {
      recognition: true,
      synthesis: true,
      inputLevel: true,
      /*
       * True, and it is the reason this transport exists. The browser speech
       * stack cannot listen while it speaks, so interruption is impossible
       * there. Here the microphone stays open the whole time and the model
       * decides when somebody is talking over it.
       */
      fullDuplex: true,
      bargeIn: true,
      endpointing: true,
      duplex: true,
    }
  }

  /** True when a real model is answering rather than the server's stand-in. */
  get isModelBacked(): boolean {
    return this.modelBacked
  }

  /**
   * Handed in by the session once the microphone is open.
   *
   * This has to start capture, not merely remember the stream, and the reason
   * is an ordering that is easy to get wrong. The socket connects before
   * anybody has been asked for a microphone: it must, because the greeting has
   * to be audible the moment the person taps, and a browser will not grant a
   * microphone outside a gesture. So `ready` arrives while there is still no
   * stream to capture from, and if starting capture only happened there, the
   * microphone would open a moment later into a transport that had already
   * decided there was nothing to listen to.
   *
   * The symptom is precisely diagnostic and worth writing down: the assistant
   * greets you and then never hears a word, because the downlink is complete
   * and the uplink was never built.
   */
  async setStream(stream: MediaStream | null): Promise<void> {
    this.stream = stream
    if (stream === null) {
      await this.capture?.close()
      this.capture = null
      return
    }
    await this.ensureCapture()
  }

  /**
   * Builds the capture chain, once, as soon as both halves exist.
   *
   * Called from two places because either can be the one that completes the
   * pair: the socket becoming ready, or the microphone being granted. Whichever
   * happens second is the one that starts it.
   */
  private async ensureCapture(): Promise<void> {
    if (this.capture || !this.stream || !this.client) return

    const codec = this.outputCodec ?? preferredCodec()
    this.capture = await createCapture({
      stream: this.stream,
      codec,
      onFrame: (payload) => this.client?.sendAudio(this.outgoingTurn, payload),
    })
  }

  setProfile(profile: ProfileContext | undefined): void {
    this.profile = profile
    if (this.client && profile) this.client.updateProfile(profile)
  }

  async connect(options: ConnectOptions): Promise<void> {
    this.locale = options.locale
    if (options.persona) this.persona = options.persona
    this.emit({ type: 'connection', status: 'connecting' })
    this.emit({ type: 'state', state: 'connecting' })

    const codec = preferredCodec()
    const input: AudioFormat = { codec, sampleRate: CAPTURE_SAMPLE_RATE }
    /*
     * The output rate offered is what the server sends today. It answers with
     * what it will actually use, and the playback graph is built from that
     * answer rather than from this guess, so a server that changes it does not
     * need every client updated first.
     */
    const offered: AudioFormat = { codec, sampleRate: 24000 }

    this.client = createRealtimeClient({
      url: this.api.voiceUrl,
      token: () => this.api.token(),
      locale: this.locale as 'en' | 'ha',
      input,
      output: offered,
      // A function, not the value: a reconnect that reinstated the persona in
      // force when the socket was first opened would undo a choice made in
      // Settings while the connection was down.
      persona: () => this.persona,
      profile: this.profile,
      greet: true,
      ...(this.socketFactory === undefined ? {} : { socketFactory: this.socketFactory }),
      handlers: {
        ready: (event) => void this.onReady(event),
        state: (state) => this.onState(state),
        transcript: (event) =>
          this.emit({
            type: 'transcript',
            speaker: event.role === 'user' ? 'user' : 'namu',
            text: event.text,
            final: event.final,
          }),
        speechBegin: () => this.emit({ type: 'speech_start', speaker: 'namu' }),
        audio: (turn, payload) => this.playback?.push(turn, payload),
        speechEnd: (event) => {
          // The assistant's turn is over on the wire, but the audio for it is
          // still in the playback queue. Reporting the end now would flip the
          // orb back to listening while it is plainly still talking.
          this.emit({ type: 'speech_end', speaker: 'namu', interrupted: event.interrupted })
        },
        patch: (profile) => {
          this.profile = { ...this.profile, ...profile }
        },
        failed: (event) => this.onFailed(event),
        disconnected: ({ willRetry }) => {
          this.connected = false
          this.emit({ type: 'connection', status: willRetry ? 'reconnecting' : 'closed' })
          if (willRetry) this.emit({ type: 'state', state: 'reconnecting' })
        },
        closed: () => {
          this.connected = false
          this.emit({ type: 'connection', status: 'closed' })
          this.emit({ type: 'state', state: 'ended' })
        },
      },
    })

    try {
      await this.client.connect()
    } catch (cause) {
      throw new VoiceError('network', 'Could not reach Namu.', { cause, recoverable: true })
    }
  }

  private async onReady(event: {
    output: AudioFormat
    model: { id: string; backed: boolean }
  }): Promise<void> {
    this.connected = true
    this.modelBacked = event.model.backed
    this.emit({ type: 'connection', status: 'open' })

    // Built from what the server said it will send, not from what was asked
    // for. Guessing here produces audio at the wrong speed, which sounds like
    // the model rather than like a bug.
    this.playback ??= await createPlayback({ format: event.output })

    // Remembered so that a microphone granted after this point builds its
    // capture chain in the format the server actually negotiated.
    this.outputCodec = event.output.codec

    // Only starts if the microphone is already open, which it is on a resume
    // but is not on a first connection. `setStream` covers the other order.
    await this.ensureCapture()

    /*
     * The application-level keepalive. A browser cannot see the WebSocket's own
     * ping frames, so without this a connection that has silently died looks
     * exactly like one where nobody is talking, and on a mobile network that
     * happens constantly.
     */
    this.keepalive ??= setInterval(() => this.client?.ping(), 15_000)
  }

  private onState(state: 'idle' | 'listening' | 'thinking' | 'speaking'): void {
    switch (state) {
      case 'listening':
        this.emit({ type: 'state', state: 'listening' })
        return
      case 'thinking':
        this.emit({ type: 'state', state: 'processing' })
        return
      case 'speaking':
        this.emit({ type: 'state', state: 'model_speaking' })
        return
      case 'idle':
        this.emit({ type: 'state', state: 'idle' })
        return
    }
  }

  private onFailed(event: { code: string; message: string; fatal: boolean }): void {
    // The server's taxonomy is richer than the client's, so this narrows it to
    // the handful of things the interface can actually show, and never invents
    // a category that would send somebody down the wrong recovery path.
    const code = event.code === 'model_unavailable' || event.code === 'model_error'
      ? 'recognition-failed'
      : event.code === 'unauthorized' || event.code === 'token_expired'
        ? 'unknown'
        : 'network'

    if (event.code === 'token_expired' || event.code === 'unauthorized') {
      // The token went stale. Throwing it away means the next attempt fetches a
      // fresh one rather than reconnecting with the same rejected credential.
      this.api.invalidateToken()
    }

    this.emit({
      type: 'error',
      error: new VoiceError(code, event.message, { recoverable: !event.fatal }),
    })
  }

  async disconnect(): Promise<void> {
    if (this.keepalive) clearInterval(this.keepalive)
    this.keepalive = null

    this.client?.close('done')
    this.client = null
    this.connected = false

    await this.capture?.close()
    this.capture = null
    await this.playback?.close()
    this.playback = null
  }

  /**
   * Nothing to do, and that is correct rather than unfinished.
   *
   * On this transport the words come from the model. An app-authored line
   * spoken here would be a second voice talking over the first, so this
   * resolves immediately and says it produced nothing.
   */
  async speak(_text: string, _options?: SpeakOptions): Promise<SpeakResult> {
    return Promise.resolve({ completed: true, durationMs: 0 })
  }

  cancelSpeech(): void {
    // A real interruption, in both places it has to happen: the server stops
    // the model, and the playback queue drops what is already buffered. Doing
    // only the first leaves a second of the abandoned answer still to play.
    this.client?.interrupt()
    this.playback?.stop()
  }

  /**
   * Also nothing to do. The microphone is already open and streaming; there is
   * no turn to wait for, because the model decides when a turn ended.
   */
  async listen(_options?: ListenOptions): Promise<ListenResult> {
    return Promise.resolve({ status: 'aborted' })
  }

  endTurn(): void {
    this.client?.endTurn()
  }

  /** Called by the screen when its own detector hears somebody start. */
  startTurn(): void {
    this.outgoingTurn += 1
    this.client?.startTurn()
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return
    this.muted = muted
    // Both ends. Pausing capture stops the bytes leaving the phone, which is
    // what somebody pressing mute actually wants; telling the server stops it
    // acting on anything already in flight.
    if (muted) this.capture?.pause()
    else this.capture?.resume()
    this.client?.setMuted(muted)
  }

  setHeld(held: boolean): void {
    if (held) this.playback?.stop()
    this.client?.setHeld(held)
  }

  setLocale(locale: LocaleCode): void {
    this.locale = locale
  }

  /**
   * Told to the open session, and kept for the next one.
   *
   * Both halves are needed. The `context` frame moves the live conversation
   * from the next turn, and the field it stores is what a reconnect will ask
   * for: without it, a socket that drops after the change comes back speaking
   * as whoever answered when the conversation started.
   */
  setPersona(persona: PersonaId): void {
    this.persona = persona
    this.client?.setPersona(persona)
  }

  /** The persona that will be asked for. Read by tests. */
  get activePersona(): PersonaId {
    return this.persona
  }

  setRate(_rate: number): void {
    // The model's own speaking rate is not something this protocol exposes,
    // and pitching the audio locally to fake it would make it sound wrong.
  }

  submitTyped(_text: string): void {
    // There is no typing in this product. Kept because the interface has it.
  }

  subscribe(listener: VoiceListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  get isConnected(): boolean {
    return this.connected
  }

  private emit(event: VoiceEvent): void {
    for (const listener of this.listeners) listener(event)
  }
}
